// Editable task field cells shared by List / Table rows, Board cards and the task modal.
import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { CalendarPlus, Check, Hourglass, Plus, Tag as TagIcon, UserRoundPlus } from '@/components/ui/icons';
import type { Task } from '@shared/types';
import { useDb, useDbShallow } from '@/lib/store';
import { effectiveStatuses, statusOf } from '@/lib/hierarchy';
import { updateTask } from '@/lib/actions';
import { dueTone, formatDue, formatDuration, parseDuration } from '@/lib/format';
import { isCompleteType } from '@shared/constants';
import { Popover } from '@/components/ui/Popover';
import { AvatarStack } from '@/components/ui/Avatar';
import { Icon } from '@/components/ui/Icon';
import { MenuSearch } from '@/components/ui/Menu';
import { StatusMenu, StatusPill } from './Status';
import { PriorityFlag, PriorityMenu } from './Priority';
import { PeopleMenu } from './People';
import { DateRangeMenu } from './DatePicker';
import { TagChip, TagMenu } from './Tags';

export function StatusCell({ task, size = 'md', iconAfter }: { task: Task; size?: 'sm' | 'md' | 'lg'; iconAfter?: boolean }) {
  const status = useDb((db) => statusOf(db, task));
  const statuses = useDbShallow((db) => effectiveStatuses(db, task.listId));
  return (
    <Popover
      content={(close) => (
        <StatusMenu
          statuses={statuses}
          currentId={task.statusId}
          onPick={(s) => {
            updateTask(task.id, { statusId: s.id });
            close();
          }}
        />
      )}
    >
      <button className="cell-button status-cell">
        <StatusPill status={status} statuses={statuses} size={size} iconAfter={iconAfter} />
      </button>
    </Popover>
  );
}

export function AssigneeCell({ task, size = 24, max = 3, className }: { task: Task; size?: number; max?: number; className?: string }) {
  return (
    <Popover content={() => <PeopleMenu selected={task.assigneeIds} onChange={(ids) => updateTask(task.id, { assigneeIds: ids })} />}>
      <button className={clsx('cell-button assignee-cell', className)}>
        <AvatarStack userIds={task.assigneeIds} size={size} max={max} />
      </button>
    </Popover>
  );
}

export function DueDateCell({ task, showEmpty = true, label, focus = 'due' }: { task: Task; showEmpty?: boolean; label?: boolean; focus?: 'start' | 'due' }) {
  const closed = useDb((db) => isCompleteType(statusOf(db, task).type));
  const tone = dueTone(task.dueDate, closed);
  return (
    <Popover
      content={(close) => (
        <DateRangeMenu
          start={task.startDate}
          due={task.dueDate}
          initialFocus={focus}
          onChange={(startDate, dueDate) => updateTask(task.id, { startDate, dueDate })}
          onClose={close}
        />
      )}
    >
      <button className={clsx('cell-button due-cell', `due-${tone}`)}>
        {task.dueDate ? (
          <span>{formatDue(task.dueDate)}</span>
        ) : showEmpty ? (
          <span className="cell-empty-icon">
            <CalendarPlus size={15} />
            {label && <span>Due date</span>}
          </span>
        ) : null}
      </button>
    </Popover>
  );
}

export function StartDateCell({ task }: { task: Task }) {
  return (
    <Popover
      content={(close) => (
        <DateRangeMenu
          start={task.startDate}
          due={task.dueDate}
          initialFocus="start"
          onChange={(startDate, dueDate) => updateTask(task.id, { startDate, dueDate })}
          onClose={close}
        />
      )}
    >
      <button className="cell-button due-cell">
        {task.startDate ? <span>{formatDue(task.startDate)}</span> : <CalendarPlus size={15} className="cell-empty-icon" />}
      </button>
    </Popover>
  );
}

export function PriorityCell({ task, showLabel = false }: { task: Task; showLabel?: boolean }) {
  return (
    <Popover
      content={(close) => (
        <PriorityMenu
          value={task.priority}
          onPick={(p) => {
            updateTask(task.id, { priority: p });
            close();
          }}
        />
      )}
    >
      <button className="cell-button priority-cell">
        <PriorityFlag priority={task.priority} showLabel={showLabel} />
      </button>
    </Popover>
  );
}

export function TagsCell({ task, spaceId, wrap }: { task: Task; spaceId: string; wrap?: boolean }) {
  return (
    <Popover content={() => <TagMenu spaceId={spaceId} selected={task.tagIds} onChange={(ids) => updateTask(task.id, { tagIds: ids })} />}>
      <button className={clsx('cell-button tags-cell', { wrap })}>
        {task.tagIds.length ? task.tagIds.map((id) => <TagChip key={id} tagId={id} small />) : <TagIcon size={15} className="cell-empty-icon" />}
      </button>
    </Popover>
  );
}

export function TimeEstimateEditor({ task, close }: { task: Task; close: () => void }) {
  const [v, setV] = useState(task.timeEstimate ? formatDuration(task.timeEstimate) : '');
  const commit = () => {
    const ms = parseDuration(v);
    updateTask(task.id, { timeEstimate: ms });
    close();
  };
  return (
    <div className="menu" style={{ width: 240 }}>
      <div className="menu-label">Time estimate</div>
      <input
        className="input"
        autoFocus
        placeholder="e.g. 3h 20m"
        value={v}
        onChange={(e) => setV(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && commit()}
      />
      <div className="row gap-6" style={{ marginTop: 8, justifyContent: 'flex-end' }}>
        {task.timeEstimate !== null && (
          <button
            className="btn sm"
            onClick={() => {
              updateTask(task.id, { timeEstimate: null });
              close();
            }}
          >
            Clear
          </button>
        )}
        <button className="btn sm primary" onClick={commit}>
          Save
        </button>
      </div>
    </div>
  );
}

export function TimeEstimateCell({ task, label }: { task: Task; label?: string }) {
  return (
    <Popover content={(close) => <TimeEstimateEditor task={task} close={close} />}>
      <button className="cell-button">
        {task.timeEstimate ? <span>{formatDuration(task.timeEstimate)}</span> : label ? <span className="faint">{label}</span> : <Hourglass size={15} className="cell-empty-icon" />}
      </button>
    </Popover>
  );
}

export function TaskTypeIcon({ task, size = 15 }: { task: Pick<Task, 'taskTypeId'>; size?: number }) {
  const tt = useDb((db) => (task.taskTypeId ? db.taskTypes[task.taskTypeId] : undefined));
  if (!tt || tt.builtin === 'task') return null;
  return <Icon name={tt.icon} size={size} color={tt.color ?? 'var(--text-2)'} fill={tt.builtin === 'milestone' ? 'none' : undefined} className="task-type-icon" />;
}

export function TaskTypeMenu({ value, onPick, workspaceId }: { value: string | null; onPick: (id: string | null) => void; workspaceId: string }) {
  const types = useDb((db) => db.taskTypes);
  const [q, setQ] = useState('');
  const list = useMemo(
    () => Object.values(types).filter((t) => t.workspaceId === workspaceId && (!q || t.name.toLowerCase().includes(q.toLowerCase()))).sort((a, b) => a.orderIndex - b.orderIndex),
    [types, workspaceId, q],
  );
  return (
    <div className="menu" style={{ width: 240 }}>
      <MenuSearch value={q} onChange={setQ} />
      <div className="menu-label">Task Types</div>
      {list.map((t) => {
        const selected = (value ?? null) === (t.builtin === 'task' ? null : t.id) || value === t.id;
        return (
          <button key={t.id} className="menu-item" onClick={() => onPick(t.builtin === 'task' ? null : t.id)}>
            <span className="menu-item-icon">
              <Icon name={t.icon} size={15} color={t.color ?? undefined} />
            </span>
            <span className="menu-item-label">{t.name}</span>
            {t.builtin === 'task' && <span className="menu-item-hint">(default)</span>}
            {selected && <Check size={14} className="faint" />}
          </button>
        );
      })}
    </div>
  );
}

export function EmptyAssignee({ size = 15 }: { size?: number }) {
  return <UserRoundPlus size={size} className="cell-empty-icon" />;
}

export function AddIcon() {
  return <Plus size={14} />;
}
